import React, { useState } from "react";
import styles from "./PartnerLogin.module.css";
import { useNavigate, useParams } from "react-router-dom";

/* =========================================================
   API
========================================================= */

const API_URL =
  "https://coinyoco.world/brobizee-api/partner_login.php";

/* =========================================================
   COMPONENT
========================================================= */

export default function PartnerLogin({
  onBack,
  onLogin,
  onCreateAccount,
  onForgotPassword,
}) {
  const navigate = useNavigate();

  /* =========================================================
     URL PARAMETERS

     Example:

     /1/87f78c6c-1d51-4bf6-8e84-64bd8ef773e2/partnerlogin

     userId     = 1
     deviceUuid = 87f78c6c-1d51-4bf6-8e84-64bd8ef773e2
  ========================================================= */

  const { userId, deviceUuid } = useParams();

  /* =========================================================
     BASE PATH
  ========================================================= */

  const basePath =
    userId && deviceUuid
      ? `/${userId}/${deviceUuid}`
      : "";

  /* =========================================================
     FORM STATE
  ========================================================= */

  const [loginValue, setLoginValue] = useState("");

  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] =
    useState(false);

  const [rememberMe, setRememberMe] =
    useState(false);

  /* =========================================================
     ERROR STATE
  ========================================================= */

  const [errors, setErrors] = useState({
    login: "",
    password: "",
  });

  /* =========================================================
     LOADING
  ========================================================= */

  const [isSubmitting, setIsSubmitting] =
    useState(false);

  /* =========================================================
     EMAIL DETECTION
  ========================================================= */

  const isEmail =
    loginValue.includes("@");

  /* =========================================================
     EMAIL VALIDATION
  ========================================================= */

  const validateEmail = (email) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
      email.trim()
    );
  };

  /* =========================================================
     MOBILE VALIDATION

     Valid examples:

     9876543210
     8765432109
     7654321098
     6543210987

     Invalid:

     1234567890
     987654321
     98765432101
  ========================================================= */

  const validateMobile = (mobile) => {
    return /^[6-9]\d{9}$/.test(
      mobile.trim()
    );
  };

  /* =========================================================
     FORM VALIDATION
  ========================================================= */

  const validateForm = () => {
    const newErrors = {
      login: "",
      password: "",
    };

    const value =
      loginValue.trim();

    /* =====================================================
       LOGIN VALIDATION
    ===================================================== */

    if (!value) {
      newErrors.login =
        "Mobile number or email is required.";
    }

    /* =====================================================
       EMAIL VALIDATION
    ===================================================== */

    else if (value.includes("@")) {
      if (!validateEmail(value)) {
        newErrors.login =
          "Please enter a valid email address.";
      }
    }

    /* =====================================================
       MOBILE VALIDATION
    ===================================================== */

    else {
      const mobile =
        value.replace(/\D/g, "");

      if (!validateMobile(mobile)) {
        newErrors.login =
          "Please enter a valid 10-digit mobile number.";
      }
    }

    /* =====================================================
       PASSWORD VALIDATION
    ===================================================== */

    if (!password.trim()) {
      newErrors.password =
        "Password is required.";
    } else if (password.length < 6) {
      newErrors.password =
        "Password must be at least 6 characters.";
    }

    /* =====================================================
       SET ERRORS
    ===================================================== */

    setErrors(newErrors);

    return (
      !newErrors.login &&
      !newErrors.password
    );
  };

  /* =========================================================
     LOGIN FIELD CHANGE
  ========================================================= */

  const handleLoginChange = (e) => {
    const value =
      e.target.value;

    setLoginValue(value);

    /* Clear login error */

    if (errors.login) {
      setErrors((prev) => ({
        ...prev,
        login: "",
      }));
    }
  };

  /* =========================================================
     PASSWORD CHANGE
  ========================================================= */

  const handlePasswordChange = (e) => {
    const value =
      e.target.value;

    setPassword(value);

    /* Clear password error */

    if (errors.password) {
      setErrors((prev) => ({
        ...prev,
        password: "",
      }));
    }
  };

  /* =========================================================
     BACK
  ========================================================= */

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    navigate(-1);
  };

  /* =========================================================
     LOGIN API
  ========================================================= */

  const loginWithDatabase = async () => {
    const cleanLogin =
      loginValue.trim();

    const loginType =
      cleanLogin.includes("@")
        ? "email"
        : "mobile";

    const cleanMobile =
      cleanLogin.replace(
        /\D/g,
        ""
      );

    const requestData = {
      user_id: userId,

      device_uuid: deviceUuid,

      login: cleanLogin,

      login_type: loginType,

      email:
        loginType === "email"
          ? cleanLogin
          : "",

      mobile_number:
        loginType === "mobile"
          ? cleanMobile
          : "",

      password: password,
    };

    console.log(
      "PARTNER LOGIN REQUEST:",
      requestData
    );

    /* =====================================================
       API REQUEST
    ===================================================== */

    const response =
      await fetch(
        API_URL,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body:
            JSON.stringify(
              requestData
            ),
        }
      );

    /* =====================================================
       RESPONSE TEXT
    ===================================================== */

    const responseText =
      await response.text();

    console.log(
      "PARTNER LOGIN RESPONSE:",
      responseText
    );

    /* =====================================================
       PARSE JSON
    ===================================================== */

    let result;

    try {
      result =
        JSON.parse(
          responseText
        );
    } catch (error) {
      throw new Error(
        "Server returned an invalid response."
      );
    }

    /* =====================================================
       HTTP ERROR
    ===================================================== */

    if (!response.ok) {
      throw new Error(
        result?.message ||
          `Server error: ${response.status}`
      );
    }

    /* =====================================================
       API SUCCESS CHECK
    ===================================================== */

    if (!result?.success) {
      throw new Error(
        result?.message ||
          "Invalid login credentials."
      );
    }

    return result;
  };

  /* =========================================================
     LOGIN SUBMIT
  ========================================================= */

  const handleLogin = async (e) => {
    e.preventDefault();

    /* =====================================================
       CLEAR OLD ERRORS
    ===================================================== */

    setErrors({
      login: "",
      password: "",
    });

    /* =====================================================
       URL CHECK
    ===================================================== */

    if (!userId || !deviceUuid) {
      setErrors({
        login:
          "User ID or Device UUID is missing from the URL.",
        password: "",
      });

      return;
    }

    /* =====================================================
       FRONTEND VALIDATION
    ===================================================== */

    const isValid =
      validateForm();

    if (!isValid) {
      return;
    }

    try {
      setIsSubmitting(true);

      /* ===================================================
         DATABASE LOGIN
      =================================================== */

      const result =
        await loginWithDatabase();

      console.log(
        "LOGIN SUCCESS:",
        result
      );

      /* ===================================================
         SAVE REMEMBER ME
      =================================================== */

      if (rememberMe) {
        localStorage.setItem(
          "partnerRememberMe",
          "true"
        );

        localStorage.setItem(
          "partnerLogin",
          loginValue.trim()
        );
      } else {
        localStorage.removeItem(
          "partnerRememberMe"
        );

        localStorage.removeItem(
          "partnerLogin"
        );
      }

      /* ===================================================
         SAVE AUTH SESSION
      =================================================== */

      localStorage.setItem(
        "partnerLoggedIn",
        "true"
      );

      localStorage.setItem(
        "partnerUserId",
        userId
      );

      localStorage.setItem(
        "partnerDeviceUuid",
        deviceUuid
      );

      /* ===================================================
         SAVE PARTNER DATA
      =================================================== */

      if (result?.partner) {
        localStorage.setItem(
          "partnerProfile",
          JSON.stringify(
            result.partner
          )
        );

        localStorage.setItem(
          `partnerProfile_${userId}_${deviceUuid}`,
          JSON.stringify(
            result.partner
          )
        );
      }

      /* ===================================================
         PARENT CALLBACK
      =================================================== */

      if (onLogin) {
        await onLogin({
          login:
            loginValue.trim(),

          password,

          rememberMe,

          loginType:
            isEmail
              ? "email"
              : "mobile",

          userId,

          deviceUuid,

          partner:
            result.partner ||
            null,
        });
      }

      /* ===================================================
         SUCCESS NAVIGATION
      =================================================== */

      if (basePath) {
        navigate(
          `${basePath}/partnerhome`,
          {
            replace: true,
          }
        );
      } else {
        navigate(
          "/partnerhome",
          {
            replace: true,
          }
        );
      }

    } catch (error) {
      console.error(
        "Partner login error:",
        error
      );

      /* ===================================================
         CLEAR AUTH STATE
      =================================================== */

      localStorage.removeItem(
        "partnerLoggedIn"
      );

      /* ===================================================
         API ERROR
      =================================================== */

      const message =
        error?.message ||
        "Login failed. Please check your credentials.";

      /* ===================================================
         SHOW ERROR BELOW LOGIN FIELD
      =================================================== */

      setErrors({
        login: message,
        password: "",
      });

    } finally {
      setIsSubmitting(false);
    }
  };

  /* =========================================================
     CREATE ACCOUNT
  ========================================================= */

  const handleCreateAccount = () => {
    if (onCreateAccount) {
      onCreateAccount();
      return;
    }

    if (basePath) {
      navigate(
        `${basePath}/partnerlogininfo`
      );
    } else {
      navigate(
        "/partnerlogininfo"
      );
    }
  };

  /* =========================================================
     FORGOT PASSWORD
  ========================================================= */

  const handleForgotPassword = () => {
    if (onForgotPassword) {
      onForgotPassword();
      return;
    }

    if (basePath) {
      navigate(
        `${basePath}/forgot-password`
      );
    } else {
      navigate(
        "/forgot-password"
      );
    }
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div
      className={
        styles.screenWrapper
      }
    >

      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className={`${styles.glowOrb} ${styles.orbOne}`}
      />

      <div
        className={`${styles.glowOrb} ${styles.orbTwo}`}
      />

      {/* =====================================================
          DEVICE FRAME
      ====================================================== */}

      <div
        className={
          styles.deviceFrame
        }
      >

        <div
          className={
            styles.glassHighlight
          }
        />

        <div
          className={
            styles.scrollContainer
          }
        >

          {/* =================================================
              HEADER
          ================================================= */}

          <header
            className={
              styles.header
            }
          >

            <button
              type="button"
              className={
                styles.backButton
              }
              onClick={
                handleBack
              }
              aria-label="Go Back"
            >

              <svg
                width="22"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0A529B"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >

                <path d="M9 14L4 9l5-5" />

                <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v1.5" />

              </svg>

            </button>

          </header>

          {/* =================================================
              MAIN
          ================================================= */}

          <main
            className={
              styles.mainContent
            }
          >

            {/* =================================================
                BRAND
            ================================================= */}

            <div
              className={
                styles.brandSection
              }
            >

              <div
                className={
                  styles.brandBadge
                }
              >

                <span
                  className={
                    styles.badgeDot
                  }
                />

                PARTNER LOGIN

              </div>

              <h1
                className={
                  styles.title
                }
              >

                <span
                  className={
                    styles.orangeText
                  }
                >
                  Welcome
                </span>

                <br />

                <span
                  className={
                    styles.blueText
                  }
                >
                  Back Partner
                </span>

              </h1>

              <p
                className={
                  styles.subtitle
                }
              >
                Login to manage your
                services, bookings and
                connect with your
                customers.
              </p>

            </div>

            {/* =================================================
                LOGIN FORM
            ================================================= */}

            <form
              className={
                styles.loginForm
              }
              onSubmit={
                handleLogin
              }
              noValidate
            >

              {/* =============================================
                  MOBILE / EMAIL
              ============================================== */}

              <div
                className={
                  styles.fieldGroup
                }
              >

                <label
                  className={
                    styles.fieldLabel
                  }
                >
                  Mobile / Email
                </label>

                <div
                  className={`${styles.inputWrapper} ${
                    errors.login
                      ? styles.inputError
                      : ""
                  }`}
                >

                  <div
                    className={
                      styles.inputIcon
                    }
                  >

                    {isEmail ? (

                      /* EMAIL ICON */

                      <svg
                        width="19"
                        height="19"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >

                        <rect
                          x="3"
                          y="5"
                          width="18"
                          height="14"
                          rx="2"
                        />

                        <polyline points="3 7 12 13 21 7" />

                      </svg>

                    ) : (

                      /* MOBILE ICON */

                      <svg
                        width="19"
                        height="19"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >

                        <rect
                          x="5"
                          y="2"
                          width="14"
                          height="20"
                          rx="2"
                        />

                        <line
                          x1="12"
                          y1="18"
                          x2="12.01"
                          y2="18"
                        />

                      </svg>

                    )}

                  </div>

                  <input
                    type="text"
                    value={
                      loginValue
                    }
                    onChange={
                      handleLoginChange
                    }
                    placeholder="Enter mobile or email"
                    className={
                      styles.inputField
                    }
                    autoComplete="username"
                  />

                </div>

                {/* LOGIN ERROR */}

                {errors.login && (
                  <span
                    className={
                      styles.errorText
                    }
                  >
                    {errors.login}
                  </span>
                )}

              </div>

              {/* =============================================
                  PASSWORD
              ============================================== */}

              <div
                className={
                  styles.fieldGroup
                }
              >

                <label
                  className={
                    styles.fieldLabel
                  }
                >
                  Password
                </label>

                <div
                  className={`${styles.inputWrapper} ${
                    errors.password
                      ? styles.inputError
                      : ""
                  }`}
                >

                  <div
                    className={
                      styles.inputIcon
                    }
                  >

                    <svg
                      width="19"
                      height="19"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >

                      <rect
                        x="3"
                        y="11"
                        width="18"
                        height="10"
                        rx="2"
                      />

                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />

                    </svg>

                  </div>

                  <input
                    type={
                      showPassword
                        ? "text"
                        : "password"
                    }
                    value={
                      password
                    }
                    onChange={
                      handlePasswordChange
                    }
                    placeholder="Enter password"
                    className={`${styles.inputField} ${styles.passwordInput}`}
                    autoComplete="current-password"
                  />

                  {/* PASSWORD TOGGLE */}

                  <button
                    type="button"
                    className={
                      styles.passwordToggle
                    }
                    onClick={() =>
                      setShowPassword(
                        (prev) =>
                          !prev
                      )
                    }
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                  >

                    {showPassword ? (

                      /* HIDE PASSWORD */

                      <svg
                        width="19"
                        height="19"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >

                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />

                        <circle
                          cx="12"
                          cy="12"
                          r="3"
                        />

                        <line
                          x1="3"
                          y1="3"
                          x2="21"
                          y2="21"
                        />

                      </svg>

                    ) : (

                      /* SHOW PASSWORD */

                      <svg
                        width="19"
                        height="19"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >

                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />

                        <circle
                          cx="12"
                          cy="12"
                          r="3"
                        />

                      </svg>

                    )}

                  </button>

                </div>

                {/* PASSWORD ERROR */}

                {errors.password && (
                  <span
                    className={
                      styles.errorText
                    }
                  >
                    {errors.password}
                  </span>
                )}

              </div>

              {/* =============================================
                  REMEMBER + FORGOT
              ============================================== */}

              <div
                className={
                  styles.optionsRow
                }
              >

                {/* REMEMBER ME */}

                <label
                  className={
                    styles.rememberLabel
                  }
                >

                  <input
                    type="checkbox"
                    checked={
                      rememberMe
                    }
                    onChange={(e) =>
                      setRememberMe(
                        e.target.checked
                      )
                    }
                    className={
                      styles.checkbox
                    }
                  />

                  <span
                    className={
                      styles.customCheckbox
                    }
                  >

                    {rememberMe && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >

                        <polyline points="20 6 9 17 4 12" />

                      </svg>
                    )}

                  </span>

                  <span>
                    Remember me
                  </span>

                </label>

                {/* FORGOT PASSWORD */}

                <button
                  type="button"
                  className={
                    styles.forgotBtn
                  }
                  onClick={
                    handleForgotPassword
                  }
                >
                  Forgot Password?
                </button>

              </div>

              {/* =============================================
                  LOGIN BUTTON
              ============================================== */}

              <button
                type="submit"
                className={
                  styles.loginBtn
                }
                disabled={
                  isSubmitting
                }
              >

                <span>
                  {isSubmitting
                    ? "Checking..."
                    : "Login"}
                </span>

                <span
                  className={
                    styles.arrowCircle
                  }
                >

                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >

                    <path d="M5 12h14" />

                    <path d="m13 6 6 6-6 6" />

                  </svg>

                </span>

              </button>

            </form>

          </main>

          {/* =================================================
              FOOTER
          ================================================= */}

          <footer
            className={
              styles.footer
            }
          >

            {/* DIVIDER */}

            <div
              className={
                styles.divider
              }
            >

              <span />

              <small>
                OR
              </small>

              <span />

            </div>

            {/* CREATE ACCOUNT */}

            <button
              type="button"
              className={
                styles.createAccountBtn
              }
              onClick={
                handleCreateAccount
              }
            >
              Create a new partner account
            </button>

            {/* SECURITY */}

            <div
              className={
                styles.securityText
              }
            >

              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >

                <rect
                  x="3"
                  y="11"
                  width="18"
                  height="10"
                  rx="2"
                />

                <path d="M7 11V7a5 5 0 0 1 10 0v4" />

              </svg>

              <span>
                Secure Partner Access
              </span>

            </div>

          </footer>

        </div>

      </div>

    </div>
  );
}